// @flow

import buildOptionsKey from 'app/common/redux/reducers/resources/buildOptionsKey';
import type { Action } from 'app/common/redux/types';
import typeof { resources } from 'node/resources/routes';

type ResourceName = $Keys<resources>;

export type State = {
  [resourceName: ResourceName]: {
    [resourceOptions: string]: {
      data: mixed,
      options: Object,
      nextBookmark: string,
      error?: any,
      isPrefetch: boolean,
      prefetchHasBeenRead: boolean,
    },
  },
};

const dataReducer = (state: State = {}, action: Action): State => {
  if (action.type === 'RESOURCE_INVALIDATE') {
    const { name, options } = action.payload;
    const nextState = {
      ...state,
      [name]: {
        ...(state[name] || {}),
      },
    };
    delete nextState[name][buildOptionsKey(options)];
    if (Object.keys(nextState[name]).length === 0) {
      delete nextState[name];
    }
    return nextState;
  } else if (action.type === 'RESOURCE_FETCH_COMPLETE') {
    const { payload, error } = action;
    const { data = {}, name, nextBookmark, options, isPrefetch = false } = payload;
    return {
      ...state,
      [name]: {
        ...(state[name] || {}),
        [buildOptionsKey(options)]: {
          data,
          options,
          nextBookmark,
          error,
          isPrefetch,
          prefetchHasBeenRead: false,
        },
      },
    };
  } else if (action.type === 'RESOURCE_PREFETCH_READ') {
    const { payload } = action;
    const { name, options } = payload;
    const optionsKey = buildOptionsKey(options);
    const prevState = (state[name] || {})[optionsKey] || {};
    return {
      ...state,
      [name]: {
        ...(state[name] || {}),
        [buildOptionsKey(options)]: {
          ...prevState,
          prefetchHasBeenRead: true,
        },
      },
    };
  } else if (action.type === 'RESOURCE_FETCH_MORE_COMPLETE') {
    const { payload, error } = action;
    const { data = [], name, nextBookmark, options } = payload;
    const optionsKey = buildOptionsKey(options);

    const previousData = ((state[name] || {})[optionsKey] || {}).data;

    // NOTE(xixia): very special case for Search. This is because the endpoint returns an OBJECT, with one
    //              field results being the bookmarked array that we care about.
    if (name === 'BaseSearchResource' && previousData) {
      if (Array.isArray(previousData.results)) {
        return {
          ...state,
          [name]: {
            ...(state[name] || {}),
            [optionsKey]: {
              data: data
                ? {
                    ...previousData,
                    // $FlowFixMe: flow doesn't know data here can be an object. assumes it's an array.
                    results: [...previousData.results, ...data.results],
                  }
                : previousData,
              options,
              nextBookmark,
              error,
            },
          },
        };
      }
    } else if (Array.isArray(previousData)) {
      return {
        ...state,
        [name]: {
          ...(state[name] || {}),
          [optionsKey]: {
            data: [...previousData, ...data],
            options,
            nextBookmark,
            error,
          },
        },
      };
    }
    // There is a race condition where resource data might be invalidated before a resourceFetchMore() action
    // resolves. In that case, we ignore the resourceFetchMore payload to avoid data inconsistency.
  }
  return state;
};

export default dataReducer;
